import { 
  generateResidualRisk, 
  analyzeActionPlan, 
  saveResidualRisk 
} from '../../redux/actions/residualRiskActions';

export class ResidualRiskService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeResidualAssessment(areaName, riskFormData, riskModel, controlState, progressCallback) {
    try {
      console.log(`[ResidualRiskService] Starting residual assessment for area: ${areaName}`);
      if (progressCallback) progressCallback('residual');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state instead of stale riskModel parameter
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();
      const freshRiskModel = currentState.risks.models[this.modelId] || {};
      const areaRisks = freshRiskModel.riskFormData[areaName]?.sectionRisks || {};
      const evaluatedRisks = [];
      
      console.log(`[ResidualRiskService] 🔍 Searching for evaluated risks in area: ${areaName}`);
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        if (Array.isArray(riskArray)) {
          riskArray.forEach(risk => {
            if (risk.status === 'Saved' && risk.priority) {
              evaluatedRisks.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });
      
      console.log(`[ResidualRiskService] Found ${evaluatedRisks.length} evaluated risks`);

      // Get fresh control state instead of stale controlState parameter
      const freshControlState = currentState.controls || {};
      
      // Find risks with saved actions from fresh control state
      console.log(`[ResidualRiskService] Checking for saved controls...`);
      console.log(`[ResidualRiskService] Available savedControls keys:`, Object.keys(freshControlState?.savedControls || {}));
      
      const risksWithActions = evaluatedRisks.filter(risk => {
        // Double-check: ensure this risk is actually from the current area
        if (risk.areaName !== areaName) {
          console.log(`[ResidualRiskService] ⚠️ SKIPPING risk from wrong area: "${risk.riskName}" (area: ${risk.areaName}, expected: ${areaName})`);
          return false;
        }
        
        const allControls = freshControlState?.savedControls?.[risk.riskId] || [];
        const modelControls = allControls.filter(control => control.modelId === this.modelId);
        console.log(`[ResidualRiskService] Risk "${risk.riskName}" (${risk.riskId}): ${modelControls.length} saved controls for this model (${allControls.length} total)`);
        return modelControls.length > 0;
      });
      
      console.log(`[ResidualRiskService] Found ${risksWithActions.length} risks with actions for residual assessment`);

      let residualAssessments = 0;

      for (const risk of risksWithActions) {
        try {
          // Analyze action plan effectiveness
          await this.dispatch(analyzeActionPlan(this.modelId, risk.riskId, risk.riskName));
          
          // LIVE UPDATE: Stream action plan analysis completion
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('actionPlanAnalyzed', {
              riskName: risk.riskName,
              riskId: risk.riskId
            });
          }
          
          // Generate residual risk assessment  
          await this.dispatch(generateResidualRisk(this.modelId, risk.riskId));
          
          // Get the generated residual risk data from Redux state
          const currentState = getState();
          const residualRiskData = currentState.residualRisk?.risks?.[this.modelId]?.[risk.riskId] || {};
          
          // Save residual risk data with generated rating
          const residualData = {
            modelId: this.modelId,
            riskId: risk.riskId,
            riskName: risk.riskName,
            priority: risk.priority,
            evaluator_id: 'autopilot@riskgpt.ai',
            comments: `Generated by RiskAutopilotV2 for area: ${areaName}`,
            residual_rating: residualRiskData.residual_rating || 'Medium', // Use generated rating or fallback
            residual_explanation: residualRiskData.residual_explanation || '',
            action_plan_rating: residualRiskData.action_plan_rating || '',
            action_plan_analysis: residualRiskData.action_plan_analysis || '',
            action_plan_short_summary: residualRiskData.action_plan_short_summary || ''
          };
          
          this.dispatch(saveResidualRisk(residualData));
          residualAssessments++;
          
          // LIVE UPDATE: Stream residual risk assessment completion
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('residualRiskCalculated', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              assessedCount: residualAssessments,
              totalRisks: risksWithActions.length
            });
          }
          
        } catch (residualError) {
          console.error(`[ResidualRiskService] Residual assessment failed for ${risk.riskName}:`, residualError);
        }
      }

      return {
        risksWithActions: risksWithActions.length,
        residualAssessments,
        areaName
      };

    } catch (error) {
      console.error('[ResidualRiskService] Residual assessment failed:', error);
      throw error;
    }
  }
}